import React, { useState, useEffect, useRef } from 'react';
import { Html, PivotControls } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

function POIMarkers({ pois, visible, onPOIClick, onPOIsUpdate, sceneCamera }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [dynamicPOIs, setDynamicPOIs] = useState([]);  // POIs added via Shift+/
  const { camera } = useThree();

  // Add sphere marker at current camera position with Shift + / key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === '?' && event.shiftKey) {  // '?' is Shift + '/'
        if (!sceneCamera) return;

        const position = sceneCamera.position.clone();

        // Calculate camera direction
        const forward = new THREE.Vector3(0, 0, -1);
        const up = new THREE.Vector3(0, 1, 0);
        forward.applyQuaternion(sceneCamera.quaternion);
        up.applyQuaternion(sceneCamera.quaternion);

        const matrix = new THREE.Matrix4();
        matrix.lookAt(new THREE.Vector3(0, 0, 0), forward, up);

        const quaternion = new THREE.Quaternion();
        quaternion.setFromRotationMatrix(matrix);
        quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI));

        // Create new POI (sphere marker)
        const newPOI = {
          id: `dynamic_poi_${Date.now()}`,
          name: `POI ${dynamicPOIs.length + 1}`,
          description: `Added at camera position`,
          position: { x: position.x, y: position.y, z: position.z },
          rotation: { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w },
          color: '#00ff00',  // Green
          shape: 'sphere',
          isDynamic: true
        };

        setDynamicPOIs(prev => [...prev, newPOI]);
        console.log('POI marker added:', newPOI);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sceneCamera, dynamicPOIs]);

  // Track static POI positions (for draggable functionality)
  const [staticPOIPositions, setStaticPOIPositions] = useState({});

  // Set initial positions for static POIs
  useEffect(() => {
    if (pois && pois.length > 0) {
      const initialPositions = {};
      pois.forEach(poi => {
        if (!staticPOIPositions[poi.id]) {
          initialPositions[poi.id] = {
            position: poi.position,
            rotation: poi.rotation
          };
        }
      });
      if (Object.keys(initialPositions).length > 0) {
        setStaticPOIPositions(prev => ({ ...prev, ...initialPositions }));
      }
    }
  }, [pois]);

  // Update POI position/rotation (for PivotControls)
  const updatePOITransform = (poiId, isDynamic, matrix) => {
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    matrix.decompose(position, quaternion, scale);

    if (isDynamic) {
      // Update dynamic POI
      setDynamicPOIs(prev => prev.map(p =>
        p.id === poiId ? {
          ...p,
          position: { x: position.x, y: position.y, z: position.z },
          rotation: { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w }
        } : p
      ));
    } else {
      // Update static POI
      setStaticPOIPositions(prev => ({
        ...prev,
        [poiId]: {
          position: { x: position.x, y: position.y, z: position.z },
          rotation: { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w }
        }
      }));
    }
  };

  // Combine static POIs + dynamic POIs
  const allPOIs = [...(pois || []), ...dynamicPOIs];

  // Pass all POI information to parent component (including current positions)
  useEffect(() => {
    if (onPOIsUpdate) {
      const updatedPOIs = allPOIs.map(poi => {
        const isDynamic = poi.isDynamic;
        const currentPosition = isDynamic
          ? poi.position
          : (staticPOIPositions[poi.id]?.position || poi.position);
        const currentRotation = isDynamic
          ? poi.rotation
          : (staticPOIPositions[poi.id]?.rotation || poi.rotation);

        return {
          ...poi,
          position: currentPosition,
          rotation: currentRotation
        };
      });
      onPOIsUpdate(updatedPOIs);
    }
  }, [allPOIs, staticPOIPositions, onPOIsUpdate]);

  // Hide all POIs if visible is false
  if (!visible) {
    return null;
  }

  return (
    <>
      {allPOIs.map((poi) => {
        const isHovered = hoveredId === poi.id;
        const isBox = poi.shape === 'box';
        const isDynamic = poi.isDynamic;

        // Get current position from staticPOIPositions for static POIs
        const currentPosition = isDynamic
          ? poi.position
          : (staticPOIPositions[poi.id]?.position || poi.position);
        const currentRotation = isDynamic
          ? poi.rotation
          : (staticPOIPositions[poi.id]?.rotation || poi.rotation);

        // Create matrix for PivotControls
        const matrix = new THREE.Matrix4().compose(
          new THREE.Vector3(currentPosition.x, currentPosition.y, currentPosition.z),
          new THREE.Quaternion(currentRotation.x, currentRotation.y, currentRotation.z, currentRotation.w),
          new THREE.Vector3(1, 1, 1)
        );

        return (
          <group key={poi.id}>
            {/* Wrap POI with PivotControls for 3D manipulation (same settings as drone) */}
            <PivotControls
              anchor={[-0.75, -0.75, -0.75]}  // Same as drone
              depthTest={false}
              lineWidth={2}
              scale={50}  // Same as drone
              fixed={true}
              matrix={matrix}
              offset={[0, 0.15, 0]}  // Move gizmo slightly upward
              onDrag={(matrix) => updatePOITransform(poi.id, isDynamic, matrix)}
            >
              <group>
                {/* Marker - Box or Sphere */}
                <mesh
                  onClick={(e) => {
                    e.stopPropagation();
                    // Copy POI info to clipboard
                    const text = `${poi.name}\n${poi.description}\n\nPosition:\nX: ${currentPosition.x.toFixed(3)}\nY: ${currentPosition.y.toFixed(3)}\nZ: ${currentPosition.z.toFixed(3)}\n\nRotation:\nX: ${currentRotation.x.toFixed(3)}\nY: ${currentRotation.y.toFixed(3)}\nZ: ${currentRotation.z.toFixed(3)}\nW: ${currentRotation.w.toFixed(3)}`;
                    navigator.clipboard.writeText(text).then(() => {
                      console.log('✅ POI info copied to clipboard:', poi.name);
                    }).catch(err => {
                      console.error('❌ Failed to copy:', err);
                    });
                  }}
                  onPointerOver={(e) => {
                    e.stopPropagation();
                    setHoveredId(poi.id);
                  }}
                  onPointerOut={(e) => {
                    e.stopPropagation();
                    setHoveredId(null);
                  }}
                >
                  {isBox ? (
                    // Box
                    <boxGeometry args={[0.3, 0.3, 0.3]} />
                  ) : (
                    // Sphere
                    <sphereGeometry args={[0.15, 32, 32]} />
                  )}
                  <meshStandardMaterial
                    color={isHovered ? '#ffff00' : poi.color}
                    transparent
                    opacity={isHovered ? 1.0 : 0.8}
                  />
                </mesh>

                {/* Outline (Wireframe) */}
                <mesh>
                  {isBox ? (
                    <boxGeometry args={[0.31, 0.31, 0.31]} />
                  ) : (
                    <sphereGeometry args={[0.16, 16, 16]} />
                  )}
                  <meshBasicMaterial
                    color="#ffffff"
                    wireframe
                    transparent
                    opacity={0.4}
                  />
                </mesh>

                {/* Axes helper (same as drone) */}
                <primitive object={new THREE.AxesHelper(2)} />

                {/* Direction arrow (thick black) */}
                <group>
                  {/* Arrow shaft (thick) */}
                  <mesh position={[0, 0, 0.25]} rotation={[Math.PI / 2, 0, 0]}>
                    <cylinderGeometry args={[0.025, 0.025, 0.5, 8]} />
                    <meshBasicMaterial color={0x000000} />
                  </mesh>
                  {/* Arrow head */}
                  <mesh position={[0, 0, 0.55]} rotation={[Math.PI / 2, 0, 0]}>
                    <coneGeometry args={[0.05, 0.1, 8]} />
                    <meshBasicMaterial color={0x000000} />
                  </mesh>
                </group>

                {/* Label (moved below sphere) */}
                <Html
                  distanceFactor={10}
                  position={[0, -0.5, 0]}
                  style={{
                    pointerEvents: 'none',
                    userSelect: 'none'
                  }}
                >
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    border: isHovered ? '2px solid yellow' : `1px solid ${poi.color}`
                  }}>
                    {poi.name}
                  </div>
                </Html>

                {/* Display detailed info on hover */}
                {isHovered && (
                  <Html
                    distanceFactor={10}
                    position={[0, -1.0, 0]}
                    style={{ pointerEvents: 'none' }}
                  >
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.95)',
                      color: '#00ff00',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontSize: '9px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre',
                      border: '1px solid #00ff00'
                    }}>
                      {`${poi.description}\n\nPosition:\nX: ${currentPosition.x.toFixed(3)}\nY: ${currentPosition.y.toFixed(3)}\nZ: ${currentPosition.z.toFixed(3)}\n\nRotation:\nX: ${currentRotation.x.toFixed(3)}\nY: ${currentRotation.y.toFixed(3)}\nZ: ${currentRotation.z.toFixed(3)}\nW: ${currentRotation.w.toFixed(3)}\n\nDrag gizmo to move/rotate | Click to copy`}
                    </div>
                  </Html>
                )}
              </group>
            </PivotControls>
          </group>
        );
      })}
    </>
  );
}

export default POIMarkers;
